import type { ReactNode } from 'react';

import type { TransportMode } from '@zen/Components/TransportModeIcon/types';
import type { TableColumn } from '@zen/DesignSystem/types';
import type { DateRange } from '@zen/DesignSystem/types';
import type {
  CargoItemTypeEnum,
  CoreCargoType,
  CoreIncoterm,
  CoreModeOfTransport,
  CoreShipmentDelayStatus,
  CoreShipmentFiltersInput,
  CoreShipmentGroupByColumn,
  CoreShipmentKpiCounts,
  CoreShipmentMilestone,
  CoreShipmentSortByInput,
  CoreShipmentStage,
  CountryCode,
  DateRangeInput
} from '@zen/graphql/types.generated';
import type { SortInput } from '@zen/types';
import type { PaginatedQueryResult } from '@zen/utils/hooks/pagination/types';
import type { Optional, Undefinable } from '@zen/utils/typescript';

import type { ShipmentListQueryResult, ShipmentListQueryVariables } from './graphql/shipmentList.generated';
import type { ShipmentListRowFragment } from './graphql/shipmentListRow.generated';

export type {
  CoreShipmentFiltersInput,
  CoreShipmentKpiCounts,
  CoreShipmentSortByInput,
  DateRange,
  DateRangeInput,
  SortInput,
  TransportMode
};
export type { ShipmentKpiCountsQueryResult, ShipmentKpiCountsQueryVariables } from './graphql/shipmentKpiCounts.generated';
export type { ShipmentListQueryResult, ShipmentListQueryVariables } from './graphql/shipmentList.generated';
export {
  CargoItemTypeEnum,
  CoreCargoContainerType,
  CoreCargoType,
  CoreIncoterm,
  CoreModeOfTransport,
  CoreShipmentAirShipmentType,
  CoreShipmentDateCertainty,
  CoreShipmentDelayStatus,
  CoreShipmentGroupByColumn,
  CoreShipmentMilestone,
  CoreShipmentScope,
  CoreShipmentSortByColumn,
  CoreShipmentStage,
  CountryCode,
  SortingOrder
} from '@zen/graphql/types.generated';

export enum ShipmentListTab {
  ALL_SHIPMENTS = 'ALL_SHIPMENTS',
  CONSOLIDATIONS = 'CONSOLIDATIONS',
  TRACKED_SHIPMENTS = 'TRACKED_SHIPMENTS'
}

export interface ShipmentListTabDefinition {
  label: string;
  tab: ShipmentListTab;
}

export interface ShipmentListTabConfig extends ShipmentListTabDefinition {
  component: ReactNode;
}

export enum ShipmentScope {
  ALL = 'ALL',
  COLLECTION = 'COLLECTION',
  DELIVERY = 'DELIVERY',
  HIGH_PRIORITY = 'HIGH_PRIORITY',
  IN_TRANSIT = 'IN_TRANSIT',
  RECENTLY_DELIVERED = 'RECENTLY_DELIVERED'
}

export interface UseShipmentListScopeResult {
  activeScope: ShipmentScope;
  setActiveScope: (scope: ShipmentScope) => void;
}

export type CoreShipmentCargo = ShipmentListRowFragment;

export type ShipmentListLocation = NonNullable<CoreShipmentCargo['collectionLocation']>;

export type ShipmentListPort = NonNullable<CoreShipmentCargo['portOfLoading']>;

export type CoreShipmentGroup = ShipmentListQueryResult['coreShipments']['nodes'][number];

export interface ShipmentListFilters {
  accountIds?: string[];
  arrivalDate?: DateRange;
  cargoReadyDate?: DateRange;
  cargoTypes?: CoreCargoType[];
  delayStatuses?: CoreShipmentDelayStatus[];
  destinationCountryCodes?: CountryCode[];
  incoterms?: CoreIncoterm[];
  mainModesOfTransport?: CoreModeOfTransport[];
  milestones?: CoreShipmentMilestone[];
  originCountryCodes?: CountryCode[];
  stages?: CoreShipmentStage[];
}

export type ShipmentListVariables = Omit<ShipmentListQueryVariables, 'after' | 'before' | 'first' | 'last'>;

export interface UseShipmentsByScopeParams {
  accountId: string;
  filters: ShipmentListFilters;
  groupBy: CoreShipmentGroupByColumn;
  scope: ShipmentScope;
  searchTerm: Undefinable<string>;
  sortBy: Undefinable<CoreShipmentSortByInput[]>;
}

export type UseShipmentsByScopeResult = PaginatedQueryResult<
  ShipmentListQueryResult,
  ShipmentListQueryVariables,
  CoreShipmentGroup
>;

export interface ShipmentRowCargo {
  label: string;
  type: CargoItemTypeEnum;
}

export interface ShipmentRowOrder {
  id: string;
  reference: string;
}

export interface ShipmentRowVessel {
  name: string;
  voyage: Optional<string>;
}

export interface ShipmentRow {
  airShipmentType: Optional<string>;
  arrivalDate: Optional<string>;
  cargo: Optional<ShipmentRowCargo>;
  cargoReadyDate: Optional<string>;
  carrier: Optional<string>;
  clientReference: Optional<string>;
  collectionAddress: Optional<string>;
  collectionDate: Optional<string>;
  consignee: Optional<string>;
  consolidationReference: Optional<string>;
  containerNumber: Optional<string>;
  customer: Optional<string>;
  delayDays: Optional<number>;
  delayStatus: Optional<CoreShipmentCargo['status']['delayStatus']>;
  deliveryAddress: Optional<string>;
  deliveryDate: Optional<string>;
  departureDate: Optional<string>;
  id: string;
  incoterm: Optional<string>;
  masterAirwayBill: Optional<string>;
  masterBillOfLading: Optional<string>;
  milestone: Optional<string>;
  modeOfTransport: TransportMode;
  nestedRowItems?: ShipmentRow[];
  orders: ShipmentRowOrder[];
  portOfDischarge: Optional<string>;
  portOfLoading: Optional<string>;
  reference: string;
  shipper: Optional<string>;
  vehiclePlateNumber: Optional<string>;
  vessel: Optional<ShipmentRowVessel>;
}

export type ShipmentListColumnKey =
  | Exclude<keyof ShipmentRow, 'clientReference' | 'delayDays' | 'delayStatus' | 'id' | 'nestedRowItems'>
  | 'status';

export interface ShipmentListColumn extends TableColumn<ShipmentRow> {
  key: ShipmentListColumnKey;
}

export interface ShipmentExport {
  data: string[][];
  headers: string[];
}
